import { BsPlusLg, BsExclamationTriangle } from 'react-icons/bs'
import { v4 as uuidv4 } from 'uuid'
import ActionStatus from 'components/ActionStatus'
import { useFieldArray } from 'react-hook-form'
import { AddFields } from '../styles'
import { useEffect, useMemo, useState } from 'react'
import { WarningModal } from 'components/Modals/WarningModal'
import { hasNonEmptyValue } from 'utils/variblesHandler'
import NotificationActions from 'actions/Notification'
import { useDispatch } from 'react-redux'
import {
  IGroupActionsComponent,
  FocusedInputType,
  ToDeleteElementType,
  listOfQuestions,
  convertDeleteActionPlanToBatchRequest,
  buildActionPlanListsByNotificationMap,
  mergeFirstNotificationActionPlansFromRedux
} from '../helpers'
import { canEditActionPlan } from '..'
import ActionTable from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/ActionPlan/Children/ActionTable'

export const GroupActionsComponent = ({
  id,
  tooltipAlert,
  register,
  control,
  reset,
  getValues,
  watch,
  errors,
  currentUser,
  groupData,
  dirtyFieldsStep4,
  setValue,
  resetField,
  isDirty,
  statusIsArchivedOrFinished,
  actionPlans,
  notificationById,
  notificationStatusState,
  trigger
}: IGroupActionsComponent) => {
  //hooks
  const notificationDispatch = useDispatch()

  //state
  const [notAllowed, setNotAllowed] = useState(false)
  const [deleteWithTextModal, setDeleteWithTextModal] = useState(false)
  const [focusedInputData, setFocusedInputData] = useState<FocusedInputType>()
  const [getRemainChar, setGetRemainChar] = useState<number>()
  const [toDeleteElement, setToDeleteElement] =
    useState<ToDeleteElementType>()

  //manage dynamic form
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'fields'
  })
  //@ts-ignore
  const onFocusElement = watch(focusedInputData?.inputname)

  const isSameLocation = useMemo(() => {
    // Para grupos, sempre permitir se o usuário tem acesso à localização
    if (notificationById?.location_incident?.value) {
      return currentUser?.locations && currentUser?.locations?.filter((i) => i.sys_id === notificationById?.location_incident?.value).length > 0
    }
    // Se não tem dados da notificação, assumir que pode editar (será validado pelo backend)
    return true
  }, [currentUser, notificationById])
  
  const isSameSector = useMemo(() => {
    // Para grupos, sempre permitir se o usuário tem acesso ao setor
    if (notificationById?.location_incident?.value && notificationById?.incident_sector?.value) {
      return currentUser?.locations && currentUser?.locations?.filter((i) => i.sys_id === notificationById?.location_incident?.value)[0]?.sectors.filter((i) => i.sys_id === notificationById?.incident_sector?.value).length > 0
    }
    // Se não tem dados da notificação, assumir que pode editar (será validado pelo backend)
    return true
  }, [currentUser, notificationById])
  
  const loggedInUserIsResponsible = useMemo(() => {
    // Para grupos, o usuário logado é sempre considerado responsável
    if (notificationById?.u_assigned_to?.value) {
      return currentUser?.sys_id && currentUser?.sys_id === notificationById?.u_assigned_to?.value
    }
    // Se não tem dados da notificação, assumir que é responsável
    return true
  }, [currentUser, notificationById])

  useEffect(() => {
    if (canEditActionPlan({ loggedInUserIsResponsible, isSameLocation, isSameSector, notificationStatusState })
      .includes(currentUser?.profile) && !statusIsArchivedOrFinished) {
      setNotAllowed(false)
    } else {
      setNotAllowed(true)
    }
  }, [loggedInUserIsResponsible, isSameLocation, isSameSector, notificationStatusState, statusIsArchivedOrFinished])
  //set saved actions
  useEffect(() => {
    if (actionPlans?.length) {
      const getFieldsData = actionPlans?.map(
        (plan, index) => {
          const items = listOfQuestions.map((question) => {
            return {
              [`${question}`]: plan[question].toString()
            }
          })
          return Object.assign(
            {
              sys_id: plan.sys_id,
              id: plan.sys_id
            },
            ...items
          )
        }
      )
      reset({
        fields: getFieldsData
      }, {
        keepDirty: false,
        keepDirtyValues: false
      })
    } else if (actionPlans?.length === 0) {
      reset({
        fields: [
          {
            sys_id: '',
            id: uuidv4(),
            how: '',
            what: '',
            when: "",
            where: '',
            who: '',
            why: '',
            how_much: 'R$ 0,00'
          }
        ]
      }, {
        keepDirty: false,
        keepDirtyValues: false
      })
    }
  }, [actionPlans])

  useEffect(() => {
    // Buscar TODOS os planos de ação da primeira notificação do grupo
    if (groupData?.notifications?.length > 0) {
      const firstNotificationId = groupData.notifications[0]?.sys_id?.value
      if (firstNotificationId) {
        // Buscar todos os planos da primeira notificação
        NotificationActions.getListOfActionPlan(notificationDispatch, firstNotificationId)
      }
    } else {
      // Se não temos dados do grupo ainda, usar busca de grupo como fallback
      NotificationActions.getListOfActionPlanGroup(notificationDispatch, id)
    }
  }, [groupData, id, notificationDispatch])

  useEffect(() => {
    if (focusedInputData?.inputname) {
      const charLength = onFocusElement.length
      setGetRemainChar(focusedInputData.limit - charLength)
    }
  }, [focusedInputData, onFocusElement])

  const addField = () => {
    append({
      sys_id: "",
      id: uuidv4(),
      how: "",
      what: "",
      when: "",
      where: "",
      who: "",
      why: "",
      how_much: 'R$ 0,00'
    })
  }
  const confirmeDelete = async () => {
    if (!toDeleteElement?.actionId || !groupData || !currentUser) {
      console.error('Dados insuficientes para deletar plano de ação')
      setDeleteWithTextModal(false)
      return
    }

    try {
      // Criar o plano que será deletado baseado no actionId
      const planToDelete = {
        sys_id: toDeleteElement.actionId,
        id: toDeleteElement.actionId,
        how: '',
        what: '',
        when: '',
        where: '',
        who: '',
        why: '',
        how_much: ''
      }

      const plansByNotif = await buildActionPlanListsByNotificationMap(groupData)
      const mergedPlansByNotif = mergeFirstNotificationActionPlansFromRedux(
        groupData,
        plansByNotif,
        actionPlans?.length ? actionPlans : null
      )

      const deleteBatchRequest = convertDeleteActionPlanToBatchRequest(
        [planToDelete],
        groupData,
        currentUser,
        mergedPlansByNotif
      )


      // Usar o novo método batch para delete
      await NotificationActions.deleteActionPlanBatch(
        notificationDispatch,
        deleteBatchRequest
      )


      // Recarregar a lista de action plans do grupo
      // O useEffect (linha 98-136) vai pegar os dados atualizados e fazer reset do formulário
      if (groupData?.notifications?.length > 0) {
        const firstNotificationId = groupData.notifications[0]?.sys_id?.value
        if (firstNotificationId) {
          await NotificationActions.getListOfActionPlan(notificationDispatch, firstNotificationId)
        }
      } else {
        await NotificationActions.getListOfActionPlanGroup(notificationDispatch, id)
      }

    } catch (error) {
      console.error('Erro ao deletar plano de ação em massa:', error)
      // TODO: Implementar feedback de erro visual para o usuário
    } finally {
      setDeleteWithTextModal(false)
    }
  }
  const removeField = (index: number) => {
    const rows = getValues('fields')
    const toDeleteRow = rows.find((row, idx) => {
      return idx === index
    })
    const hasData = hasNonEmptyValue(toDeleteRow)
    if (hasData && toDeleteRow.sys_id) {
      setDeleteWithTextModal(true)
      setToDeleteElement({ index, actionId: toDeleteRow.sys_id })
    } else {
      remove(index)
    }
  }

  return (
    <div data-testid="test-group-action-component">
      {/* modals */}
  
      <WarningModal
        icon={
          <BsExclamationTriangle
            size={50}
            className="text-errorText"
          />
        }
        customStyle={{
          titleStyle: 'text-errorText'
        }}
        title="Excluir Plano de ação"
        subTitle="Você tem certeza que deseja excluir este plano de ação já registrado? Essa ação não poderá ser desfeita."
        leftButtonAction={() => {
          setDeleteWithTextModal(false)
        }}
        rightButtonAction={() => {
          confirmeDelete()
        }}
        isOpen={deleteWithTextModal}
        handleCloseModal={() => setDeleteWithTextModal(false)}
        leftButtonText="Cancelar"
        rightButtonText="Sim, excluir"
      />
      {/* header */}
      <fieldset disabled={notAllowed}>
        {tooltipAlert && (
          <div className="px-6" data-testid="test-group-step4-tooltip">
            <ActionStatus text={tooltipAlert} status={1} />
          </div>
        )}
        {/* forum table */}

        <div className="flex flex-col  mb-8">
          <ActionTable
            trigger={trigger}
            fields={fields}
            notAllowed={notAllowed}
            errors={errors}
            setFocusedInputData={setFocusedInputData}
            register={register}
            setValue={setValue}
            control={control}
            removeField={removeField}
            getValues={getValues}
          />
          {errors?.fields?.length && (
            <span className="text-errorText text-xs">
              Preenchimento obrigatório
            </span>
          )}
          {focusedInputData?.inputname && (
            <span className="ml-auto mr-8">
              {getRemainChar} caracteres
            </span>
          )}
          <AddFields
            disabled={notAllowed}
            className={`${notAllowed
              ? 'text-mediumGray border-mediumGray'
              : 'text-amberAlt'
              } `}
            data-testid="test-add-line-button"
            type="button"
            onClick={() => {
              addField()
            }}
          >
            <BsPlusLg
              className={`${notAllowed ? 'text-mediumGray' : 'text-amberAlt'
                }`}
              size={8}
            />
            Adicionar linha
          </AddFields>
        </div>
      </fieldset>
    </div>
  )
}